"use client";

import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { Water } from "three/examples/jsm/objects/Water.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { LUTPass } from "three/examples/jsm/postprocessing/LUTPass.js";

const ORCA_BASE = "/models/orca";
const SOUND_URL = (name: string) => `${ORCA_BASE}/sounds/${name}`;
const MODEL_URL = `${ORCA_BASE}/paus.glb`;

const mouseVert = `
varying vec2 vUv;

void main()
{
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const mouseFrag = `
uniform sampler2D uFlow;
uniform vec2 uMouse;
uniform vec2 uVel;
uniform float uDecay;
uniform float uInject;

varying vec2 vUv;

void main()
{
    vec2 flow = texture2D(uFlow, vUv).rg;

    /* Advection — trace backward along flow */
    vec2 back = vUv - flow;
    vec2 adv = texture2D(uFlow, clamp(back, 0.0, 1.0)).rg;

    /* Dissipation */
    adv *= uDecay;

    /* Inject mouse velocity at tip */
    vec2 dir = vUv - uMouse;
    float dist = length(dir);
    float infl = exp(-dist * dist * 120.0);
    adv += uVel * infl * uInject * 20.0;

    adv = clamp(adv, vec2(-0.3), vec2(0.3));
    gl_FragColor = vec4(adv, 0.0, 1.0);
}
`;

const defaults: Record<string, any> = {
  waterOpacity: 0.48,
  waveSpeed: 1.35,
  waveHeight: 3,
  causticIntensity: 3.45,
  causticSpeed: 1.19,
  causticScale: 0.43,
  causticShadow: true,
  causticL1Scale: 7.8,
  causticL1Speed: 2.43,
  causticL1Weight: 0.23,
  causticL1Exp: 2.95,
  causticL2Scale: 5.4,
  causticL2Speed: 1.53,
  causticL2Weight: 0.7,
  causticL2Exp: 5,
  causticL3Scale: 3.3,
  causticL3Speed: 3,
  causticL3Weight: 1,
  causticL3Exp: 5,
  causticDrift1X: 0.00799999999999999,
  causticDrift1Y: 0.035,
  causticDrift2X: -0.038,
  causticDrift2Y: -0.036,
  causticEdgeThresh: 0.25,
  causticCenterSup: 0.2,
  causticCellSpeed: 1.25,
  causticGlow1W: 0.66,
  causticGlow2W: 0.65,
  causticColor1: [1, 1, 1],
  causticColor2: [1, 1, 1],
  refraction: true,
  refractionStrength: 0.027,
  refractionSpeed: 2.25,
  bloomIntensity: 0.73,
  orcaSpeed: 0.44,
  camTransitionSpeed: 2.5,
  autoRotate: false,
  lightLoopSpeed: 0.2,
  lightAngle: 2.89840734641021,
  godRays: true,
  godRayIntensity: 3,
  godRayDecay: 1,
  godRaySamples: 94,
  particleDensity: 1.7,
  vignette: 1,
  chromaticAberration: 0.0025,
  lutIntensity: 0.31,
  flowStrength: 0.1,
  flowDecay: 0.94,
  flowInject: 1.5,
  camHeight: 1,
  camDist: 25,
  camTargetY: -7.3,
  bgmVolume: 0.47,
  waterVolume: 1,
  sfxVolume: 0.59,
  waveEnabled: true,
  waveDuration: 0.8,
  waveSize: 7,
  waveIntensity: 1,
  waveRippleSpeed: 20,
  waveColor: [0, 0.9, 1],
  waveOffsetX: 0,
  waveOffsetY: -0.4,
  waveOffsetZ: 2.2
};

export default function OrcaPage() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = mountRef.current;
    if (!root) return;

    const _origWarn = console.warn;
    console.warn = (...a: any[]) => {
      if (typeof a[0] === "string" && (a[0].includes("cloneUniforms") || a[0].includes("Textures of render targets cannot be cloned"))) return;
      _origWarn(...a);
    };

    const createdNodes: HTMLElement[] = [];

    /* ═══════ PARAMETER ═══════ */
    const params: Record<string, any> = { ...defaults };

    /* ═══════ SCENE / CAMERA / RENDERER ═══════ */
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x081828);
    scene.fog = new THREE.FogExp2(0x0a1e30, 0.025);

    const camera = new THREE.PerspectiveCamera(40, innerWidth / innerHeight, 0.1, 60);
    camera.position.set(5, 2.5, 7);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(innerWidth, innerHeight);
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    root.prepend(renderer.domElement);

    /* ═══════ AUDIO / BGM ═══════ */
    const listener = new THREE.AudioListener();
    camera.add(listener);

    const audioLoader = new THREE.AudioLoader();

    const bgm = new THREE.Audio(listener);
    const waterAmbient = new THREE.Audio(listener);
    const orcaAudio = new THREE.Audio(listener);
    const mouseAudio = new THREE.Audio(listener);
    const clickAudio = new THREE.Audio(listener);

    const _audioLoaded: Record<string, boolean> = { bgm: false, water: false, orca: false, mouse: false, click: false };
    const _audioCtx = THREE.AudioContext.getContext();
    let _mouseBuffer: AudioBuffer | null = null;
    let _clickBuffer: AudioBuffer | null = null;

    const _volMap: Record<string, string> = { bgm: "bgmVolume", water: "waterVolume", orca: "sfxVolume", mouse: "sfxVolume", click: "sfxVolume" };
    function loadAllSounds() {
      const _pairs = [
        { k: "bgm", url: SOUND_URL("bgm_instrument.mp3"), a: bgm, l: true },
        { k: "water", url: SOUND_URL("water_cinema.mp3"), a: waterAmbient, l: true },
        { k: "orca", url: SOUND_URL("orca_sound.mp3"), a: orcaAudio, l: false },
        { k: "mouse", url: SOUND_URL("mouse.mp3"), a: mouseAudio, l: false },
        { k: "click", url: SOUND_URL("click.mp3"), a: clickAudio, l: false }
      ];
      for (const p of _pairs) {
        const vk = _volMap[p.k];
        const vol = p.k === "orca" ? params[vk] * 0.7 : p.k === "mouse" || p.k === "click" ? params[vk] * 0.4 : params[vk];
        audioLoader.load(p.url, (buf) => {
          p.a.setBuffer(buf);
          p.a.setVolume(vol);
          p.a.setLoop(p.l);
          _audioLoaded[p.k] = true;
          if (p.k === "mouse") _mouseBuffer = buf;
          if (p.k === "click") _clickBuffer = buf;
        });
      }
    }

    function playOverlap(buffer: AudioBuffer, volume: number) {
      if (!buffer) return;
      if (_audioCtx.state === "suspended") _audioCtx.resume();
      const src = _audioCtx.createBufferSource();
      src.buffer = buffer;
      const gain = _audioCtx.createGain();
      gain.gain.value = volume;
      src.connect(gain);
      gain.connect(_audioCtx.destination);
      src.start();
    }

    function resumeAudio() {
      if (_audioCtx.state === "suspended") {
        _audioCtx.resume().then(playAmbient);
      } else {
        playAmbient();
      }
    }
    function playAmbient() {
      if (_audioLoaded.bgm && !bgm.isPlaying) {
        bgm.setVolume(params.bgmVolume);
        bgm.play();
      }
      if (_audioLoaded.water && !waterAmbient.isPlaying) {
        waterAmbient.setVolume(params.waterVolume);
        waterAmbient.play();
      }
    }

    const _resumePointer = () => resumeAudio();
    const _resumeKey = () => resumeAudio();
    document.addEventListener("pointerdown", _resumePointer, { once: true });
    document.addEventListener("keydown", _resumeKey, { once: true });

    loadAllSounds();

    /* ═══════ LIGHTING ═══════ */
    const lightRadius = 15;
    let lightAngle = params.lightAngle;
    const sunBasePos = new THREE.Vector3(Math.sin(lightAngle) * lightRadius, 10, Math.cos(lightAngle) * lightRadius);
    let sunDir = sunBasePos.clone().normalize();

    const sunLight = new THREE.DirectionalLight(0x99ddff, 1.2);
    sunLight.position.copy(sunBasePos);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(2048, 2048);
    sunLight.shadow.camera.near = 0.5;
    sunLight.shadow.camera.far = 30;
    const sd = 15;
    sunLight.shadow.camera.left = -sd;
    sunLight.shadow.camera.right = sd;
    sunLight.shadow.camera.top = sd;
    sunLight.shadow.camera.bottom = -sd;
    sunLight.shadow.bias = 0.001;
    sunLight.shadow.radius = 2;
    scene.add(sunLight);

    const sunSphere = new THREE.Mesh(new THREE.SphereGeometry(0.18, 12, 12), new THREE.MeshBasicMaterial({ color: 0x99ddff }));
    sunSphere.position.copy(sunBasePos);
    scene.add(sunSphere);
    scene.add(new THREE.AmbientLight(0x1a4060, 0.8));

    scene.add(new THREE.HemisphereLight(0x2a5070, 0x0a2010, 0.5));

    /* ═══════ WATER SURFACE ═══════ */
    const waterRes = 100;
    const waterGeo = new THREE.PlaneGeometry(150, 150, waterRes, waterRes);
    const wgPos = waterGeo.attributes.position;
    const waterNormals = new THREE.TextureLoader().load("https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/waternormals.jpg", (t) => {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
    });
    const water = new Water(waterGeo, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals,
      sunDirection: sunDir,
      sunColor: 0x99ddff,
      waterColor: 0x0c3450,
      distortionScale: 15,
      alpha: params.waterOpacity,
      fog: true,
      side: THREE.DoubleSide
    });
    water.rotation.x = -Math.PI / 2;
    water.position.y = 3.5;
    scene.add(water);

    /* ════════════════════════════════════════════════════════════
       CAUSTIC RENDER TARGET
       ════════════════════════════════════════════════════════════ */
    const causticRT = new THREE.WebGLRenderTarget(512, 512);
    causticRT.texture.wrapS = causticRT.texture.wrapT = THREE.RepeatWrapping;
    const causticRTCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const causticRTScene = new THREE.Scene();

    const causticGLSL = `
vec2 hash2D(vec2 p){
  return fract(sin(vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3))))*43758.5453);
}

float causticCell(vec2 p, float time, float wrap){
  vec2 n=wrap>0.0 ? mod(floor(p),vec2(wrap)) : floor(p);
  vec2 f=fract(p);
  float md=8.0, sd2=8.0;
  for(int j=-1;j<=1;j++) for(int i=-1;i<=1;i++){
    vec2 g=vec2(float(i),float(j));
    vec2 ni=wrap>0.0 ? mod(n+g,vec2(wrap)) : n+g;
    vec2 o=hash2D(ni);
    o=0.5+0.5*sin(time*uCellAnimSpeed+6.2831*o);
    vec2 r=g-f+o;
    float d=dot(r,r);
    if(d<md){sd2=md;md=d;}else if(d<sd2){sd2=d;}
  }
  float d1=sqrt(md);
  float d2=sqrt(sd2);
  float edge=d2-d1;
  return smoothstep(uEdgeThresh,0.0,edge)*smoothstep(0.0,uCenterSup,d1);
}

float softGlow(vec2 p, float time){
  vec2 q=vec2(
    sin(p.x*0.7+time*0.3)*cos(p.y*0.5-time*0.2),
    cos(p.x*0.6-time*0.25)*sin(p.y*0.8+time*0.35)
  );
  float v=sin(q.x*3.0+q.y*2.0+time*0.5)*0.5+0.5;
  return v*v;
}

vec3 getCaustics(vec2 uv, float time){
  uv=fract(uv);
  vec2 d1=vec2(time*uDrift1X,time*uDrift1Y);
  vec2 d2=vec2(time*uDrift2X,time*uDrift2Y);

  float c1=causticCell(uv*uL1Scale+d1,time*uL1Speed,uL1Scale);
  float c2=causticCell(uv*uL2Scale+d2,time*uL2Speed,ceil(uL2Scale));
  float c3=causticCell(uv*uL3Scale+d1*0.5+d2*0.3,time*uL3Speed,ceil(uL3Scale));

  float g1=softGlow(uv*4.0+d1,time*0.4)*uGlow1W;
  float g2=softGlow(uv*7.0-d2,time*0.6)*uGlow2W;

  float c=pow(c1,uL1Exp)*uL1Weight
          +pow(c2,uL2Exp)*uL2Weight
          +pow(c3,uL3Exp)*uL3Weight
          +g1+g2;
  c=clamp(c,0.0,1.0);

  vec3 col=mix(uCol1,uCol2,c);
  return col*c;
}
`;

    const causticRTMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uSpeed: { value: params.causticSpeed },
        uL1Scale: { value: params.causticL1Scale },
        uL1Speed: { value: params.causticL1Speed },
        uL1Weight: { value: params.causticL1Weight },
        uL1Exp: { value: params.causticL1Exp },
        uL2Scale: { value: params.causticL2Scale },
        uL2Speed: { value: params.causticL2Speed },
        uL2Weight: { value: params.causticL2Weight },
        uL2Exp: { value: params.causticL2Exp },
        uL3Scale: { value: params.causticL3Scale },
        uL3Speed: { value: params.causticL3Speed },
        uL3Weight: { value: params.causticL3Weight },
        uL3Exp: { value: params.causticL3Exp },
        uDrift1X: { value: params.causticDrift1X },
        uDrift1Y: { value: params.causticDrift1Y },
        uDrift2X: { value: params.causticDrift2X },
        uDrift2Y: { value: params.causticDrift2Y },
        uEdgeThresh: { value: params.causticEdgeThresh },
        uCenterSup: { value: params.causticCenterSup },
        uCellAnimSpeed: { value: params.causticCellSpeed },
        uGlow1W: { value: params.causticGlow1W },
        uGlow2W: { value: params.causticGlow2W },
        uCol1: { value: new THREE.Color().fromArray(params.causticColor1) },
        uCol2: { value: new THREE.Color().fromArray(params.causticColor2) }
      },
      vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position,1.0);}`,
      fragmentShader: `
uniform float uTime,uSpeed;
uniform float uL1Scale,uL1Speed,uL1Weight,uL1Exp;
uniform float uL2Scale,uL2Speed,uL2Weight,uL2Exp;
uniform float uL3Scale,uL3Speed,uL3Weight,uL3Exp;
uniform float uDrift1X,uDrift1Y,uDrift2X,uDrift2Y;
uniform float uEdgeThresh,uCenterSup,uCellAnimSpeed;
uniform float uGlow1W,uGlow2W;
uniform vec3 uCol1,uCol2;
varying vec2 vUv;
${causticGLSL}
void main(){gl_FragColor=vec4(getCaustics(vUv,uTime*uSpeed),1.0);}
`
    });
    causticRTScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), causticRTMat));

    /* ═══════ SEAFLOOR ═══════ */
    function noise2D(x: number, z: number) {
      return Math.sin(x * 0.08 + z * 0.05) * Math.cos(z * 0.09 - x * 0.04) * 0.5 + Math.sin(x * 0.2 + z * 0.15) * 0.25 + Math.sin(x * 0.4 + z * 0.35) * 0.12 + Math.sin(x * 0.8 + z * 0.7) * 0.06;
    }
    function makeSandTexture(sz = 512) {
      const c = document.createElement("canvas");
      c.width = c.height = sz;
      const ctx = c.getContext("2d")!,
        img = ctx.createImageData(sz, sz),
        d = img.data;
      for (let y = 0; y < sz; y++)
        for (let x = 0; x < sz; x++) {
          const i = (y * sz + x) * 4,
            n = noise2D(x * 0.02, y * 0.02) * 20 + Math.random() * 8;
          d[i] = 160 + n;
          d[i + 1] = 135 + n;
          d[i + 2] = 95 + n * 0.5;
          d[i + 3] = 255;
        }
      ctx.putImageData(img, 0, 0);
      const t = new THREE.CanvasTexture(c);
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(8, 8);
      return t;
    }

    const floorGeo = new THREE.PlaneGeometry(60, 60, 120, 120);
    const fp = floorGeo.attributes.position;
    for (let i = 0; i < fp.count; i++) fp.setY(i, noise2D(fp.getX(i), fp.getZ(i)) * 0.6);
    fp.needsUpdate = true;
    floorGeo.computeVertexNormals();

    const floorMat = new THREE.MeshStandardMaterial({
      map: makeSandTexture(),
      color: 0xc8b898,
      roughness: 0.85,
      metalness: 0.0
    });

    let floorCausticShader: any;

    floorMat.onBeforeCompile = (shader) => {
      shader.uniforms.uSunDir = { value: sunDir.clone().negate() };
      shader.uniforms.uCausticIntensity = { value: params.causticIntensity };
      shader.uniforms.uScale = { value: params.causticScale };
      shader.uniforms.uCausticShadow = { value: params.causticShadow ? 1.0 : 0.0 };
      shader.uniforms.uCausticMap = { value: causticRT.texture };

      shader.vertexShader = shader.vertexShader.replace(
        "#include <common>",
        `#include <common>
varying vec3 vWorldPos;`
      );
      shader.vertexShader = shader.vertexShader.replace(
        "#include <project_vertex>",
        `#include <project_vertex>
vWorldPos=(modelMatrix*vec4(transformed,1.0)).xyz;`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <common>",
        `#include <common>
uniform vec3 uSunDir;
uniform float uCausticIntensity;
uniform float uScale;
uniform float uCausticShadow;
uniform sampler2D uCausticMap;
varying vec3 vWorldPos;`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <dithering_fragment>",
        `#include <dithering_fragment>
{
  vec2 _cUv=(vWorldPos.xz+((3.5-vWorldPos.y)/max(abs(uSunDir.y),0.001))*uSunDir.xz)*uScale;
  vec3 _caustic=texture2D(uCausticMap,_cUv).rgb;
  #if defined(USE_SHADOWMAP)&&NUM_DIR_LIGHT_SHADOWS>0
    DirectionalLightShadow _dls=directionalLightShadows[0];
    float _shad=getShadow(directionalShadowMap[0],_dls.shadowMapSize,_dls.shadowIntensity,_dls.shadowBias,_dls.shadowRadius,vDirectionalShadowCoord[0]);
    _caustic*=mix(1.0,_shad,uCausticShadow);
  #endif
  gl_FragColor.rgb+=_caustic*uCausticIntensity;
}`
      );
      floorCausticShader = shader;
    };

    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -5;
    floor.receiveShadow = true;
    scene.add(floor);

    /* ═══════ GOD RAYS — radial blur + water lens pattern shaping ═══════ */
    const godRaySunScreen = new THREE.Vector2(0.5, 1.0);
    const _godRayVec = new THREE.Vector3();

    const godRayPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        uSunScreen: { value: godRaySunScreen },
        uWaterTime: { value: 0 },
        uIntensity: { value: params.godRayIntensity },
        uDecay: { value: params.godRayDecay },
        uSamples: { value: params.godRaySamples }
      },
      vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform vec2 uSunScreen;
    uniform float uWaterTime;
    uniform float uIntensity;
    uniform float uDecay;
    uniform float uSamples;
    varying vec2 vUv;

    float lum(vec3 c){return dot(c,vec3(0.2126,0.7152,0.0722));}

    float waterLens(vec2 p, float t){
      float v=0.0;
      v+=sin(p.x*1.3+t*1.05)*cos(p.y*1.0+t*0.75)*0.28;
      v+=sin(p.x*2.8-t*1.55)*cos(p.y*2.2+t*1.25)*0.24;
      v+=sin(p.x*5.2+t*2.15)*cos(p.y*4.3-t*1.85)*0.20;
      v+=sin(p.x*9.0-t*2.9)*cos(p.y*7.8+t*2.45)*0.15;
      v+=sin(p.x*14.0+t*3.9)*cos(p.y*12.0-t*3.3)*0.10;
      return smoothstep(-0.02,0.52,v);
    }

    void main(){
      vec3 scene=texture2D(tDiffuse,vUv).rgb;
      vec2 dir=uSunScreen-vUv;
      float dist=length(dir);
      dir=normalize(dir);

      vec3 accum=vec3(0.0);
      float w=1.0;
      float maxS=uSamples;

      for(int i=0;i<128;i++){
        if(float(i)>=maxS)break;
        float t=float(i)/(maxS-1.0)*dist;
        vec2 uv=vUv+dir*t;
        if(uv.x<0.0||uv.x>1.0||uv.y<0.0||uv.y>1.0)break;

        vec3 c=texture2D(tDiffuse,uv).rgb;
        float b=pow(max(lum(c)-0.15,0.0)/0.85,1.5);

        vec2 wCoord=uv*vec2(30.0,22.0);
        float pattern=waterLens(wCoord,uWaterTime);

        accum+=c*b*pattern*w;
        w*=uDecay;
      }

      float distFade=exp(-dist*dist*3.5);
      vec2 edge=min(vUv,1.0-vUv);
      float edgeFade=smoothstep(0.0,0.12,min(edge.x,edge.y));

      float finalFade=distFade*edgeFade;
      vec3 rayCol=mix(vec3(0.22,0.42,0.62),vec3(0.62,0.80,0.94),clamp(accum*2.5,0.0,1.0));

      gl_FragColor=vec4(scene+accum*uIntensity*finalFade+rayCol*accum*uIntensity*0.3*finalFade,1.0);
    }
  `
    });
    godRayPass.enabled = params.godRays;

    /* ═══════ PARTICLES ═══════ */
    const bubbleCount = 400;
    const bubbleGeo = new THREE.BufferGeometry();
    const bPos = new Float32Array(bubbleCount * 3);
    const bSpd = new Float32Array(bubbleCount);
    const bOff = new Float32Array(bubbleCount);
    for (let i = 0; i < bubbleCount; i++) {
      bPos[i * 3] = (Math.random() - 0.5) * 45;
      bPos[i * 3 + 1] = Math.random() * 16 - 5;
      bPos[i * 3 + 2] = (Math.random() - 0.5) * 45;
      bSpd[i] = Math.random() * 0.5 + 0.08;
      bOff[i] = Math.random() * Math.PI * 2;
    }
    bubbleGeo.setAttribute("position", new THREE.BufferAttribute(bPos, 3));
    const bubbles = new THREE.Points(
      bubbleGeo,
      new THREE.PointsMaterial({
        color: 0x7799bb,
        size: 0.05,
        transparent: true,
        opacity: 0.3,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
        depthWrite: false
      })
    );
    scene.add(bubbles);

    const debrisCount = 200;
    const debrisGeo = new THREE.BufferGeometry();
    const dPos = new Float32Array(debrisCount * 3);
    const dSpd = new Float32Array(debrisCount);
    const dOff = new Float32Array(debrisCount);
    for (let i = 0; i < debrisCount; i++) {
      dPos[i * 3] = (Math.random() - 0.5) * 40;
      dPos[i * 3 + 1] = Math.random() * 9 - 5;
      dPos[i * 3 + 2] = (Math.random() - 0.5) * 40;
      dSpd[i] = Math.random() * 0.15 + 0.02;
      dOff[i] = Math.random() * Math.PI * 2;
    }
    debrisGeo.setAttribute("position", new THREE.BufferAttribute(dPos, 3));
    const debris = new THREE.Points(
      debrisGeo,
      new THREE.PointsMaterial({
        color: 0x556677,
        size: 0.025,
        transparent: true,
        opacity: 0.2,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
        depthWrite: false
      })
    );
    scene.add(debris);

    /* ═══════ FISH ═══════ */
    const fishCount = 80;
    const fishGeo = new THREE.BufferGeometry();
    fishGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array([-0.06, 0, 0, 0.06, 0.025, 0, 0.06, -0.025, 0]), 3));
    fishGeo.computeVertexNormals();
    const fishMat = new THREE.MeshBasicMaterial({ color: 0x8898a8, side: THREE.DoubleSide, transparent: true, opacity: 0.5 });
    const fishMesh = new THREE.InstancedMesh(fishGeo, fishMat, fishCount);
    fishMesh.castShadow = true;
    fishMesh.receiveShadow = true;
    const fishD: any[] = [],
      _dum = new THREE.Object3D();
    for (let i = 0; i < fishCount; i++) {
      const a = Math.random() * Math.PI * 2,
        r = 2 + Math.random() * 8,
        h = (Math.random() - 0.5) * 5;
      fishD.push({ a, r, h, s: 0.3 + Math.random() * 0.4, p: Math.random() * Math.PI * 2 });
      _dum.position.set(Math.cos(a) * r, h, Math.sin(a) * r);
      _dum.lookAt(0, h, 0);
      _dum.updateMatrix();
      fishMesh.setMatrixAt(i, _dum.matrix);
    }
    fishMesh.instanceMatrix.needsUpdate = true;
    scene.add(fishMesh);

    /* ═══════ CONTROLS ═══════ */
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    controls.dampingFactor = 0.06;
    controls.target.set(0, 0.5, 0);
    controls.maxPolarAngle = Math.PI / 2.2;
    controls.minDistance = 4;
    controls.maxDistance = 25;

    /* ═══════ POST-PROCESSING ═══════ */
    const underCurrentPass = new ShaderPass({
      uniforms: { tDiffuse: { value: null }, uTime: { value: 0 }, uIntensity: { value: 0.2 } },
      vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader: `
uniform float uTime,uIntensity;
uniform sampler2D tDiffuse;
varying vec2 vUv;
void main(){
  vec4 c=texture2D(tDiffuse,vUv);
  float lum=dot(c.rgb,vec3(0.2126,0.7152,0.0722));
  float dist=1.0-vUv.y;

  float bl=dist*0.004;
  vec4 blr=texture2D(tDiffuse,vUv+vec2(bl,bl))*0.25+texture2D(tDiffuse,vUv-vec2(bl,bl))*0.25+c*0.5;
  c.rgb=mix(c.rgb,blr.rgb,dist*0.4);

  float fogAmt=1.0-smoothstep(0.0,0.7,vUv.y);
  fogAmt+=sin(vUv.x*4.0+vUv.y*5.0+uTime*0.1)*0.08;
  vec3 fogCol=mix(vec3(0.006,0.04,0.07),vec3(0.01,0.06,0.12),fogAmt);
  c.rgb=mix(c.rgb,fogCol,fogAmt*0.4);

  float s1=sin(vUv.x*5.0+vUv.y*4.0+uTime*0.2)*0.5+0.5;
  float s2=sin(vUv.x*7.0-vUv.y*6.0+uTime*0.3)*0.5+0.5;
  float p=s1*s2;
  float alpha=p*smoothstep(0.7,0.0,vUv.y)*(1.0-smoothstep(0.0,0.3,lum))*uIntensity;
  c.rgb+=mix(vec3(0.15,0.35,0.55),vec3(0.4,0.65,0.85),p)*alpha;
  gl_FragColor=c;
}`
    });
    underCurrentPass.enabled = true;

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(godRayPass);
    composer.addPass(underCurrentPass);

    const bloomPass = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), params.bloomIntensity, 0.5, 0.15);
    composer.addPass(bloomPass);

    const refractionPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        uTime: { value: 0 },
        uStrength: { value: params.refractionStrength },
        uSpeed: { value: params.refractionSpeed }
      },
      vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader: `uniform float uTime;uniform float uStrength;uniform float uSpeed;uniform sampler2D tDiffuse;varying vec2 vUv;void main(){vec2 uv=vUv;float t=uTime*uSpeed;float s=uStrength*100.0;uv.x+=sin(uv.y*2.0+t*1.5)*0.004*s;uv.y+=cos(uv.x*2.5+t*1.8)*0.004*s;uv.x+=sin(uv.x*3.0+uv.y*2.0+t*2.5)*0.003*s;uv.y+=cos(uv.x*2.0-uv.y*3.0+t*2.2)*0.003*s;uv.x+=sin(uv.x*5.0+uv.y*4.0+t*4.0)*0.001*s;uv.y+=cos(uv.y*5.0-uv.x*4.0+t*3.5)*0.001*s;gl_FragColor=texture2D(tDiffuse,clamp(uv,0.0,1.0));}`
    });
    refractionPass.enabled = params.refraction;
    composer.addPass(refractionPass);

    /* ── Fluid Flow + Wave Ripples from mouse ── */
    const _mouse = { x: 0.5, y: 0.5, px: 0.5, py: 0.5 };
    let _lastMouseSfx = 0;
    const _onMouseMove = (e: MouseEvent) => {
      _mouse.px = _mouse.x;
      _mouse.py = _mouse.y;
      _mouse.x = e.clientX / innerWidth;
      _mouse.y = 1 - e.clientY / innerHeight;

      const dx = _mouse.x - _mouse.px;
      const dy = _mouse.y - _mouse.py;
      const speed = Math.sqrt(dx * dx + dy * dy);
      const now = performance.now() / 1000;
      if (_mouseBuffer && speed > 0.008 && now - _lastMouseSfx > 0.06) {
        const vel = Math.min(speed * 6, 1);
        playOverlap(_mouseBuffer, vel * params.sfxVolume * 0.5);
        _lastMouseSfx = now;
      }
    };
    addEventListener("mousemove", _onMouseMove);

    /* ═══════ FLUID SIMULATION (Ping Pong RT) ═══════ */
    const FLOW_RES = 256;
    const _flowRT = [
      new THREE.WebGLRenderTarget(FLOW_RES, FLOW_RES, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        type: THREE.HalfFloatType
      }),
      new THREE.WebGLRenderTarget(FLOW_RES, FLOW_RES, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        type: THREE.HalfFloatType
      })
    ];
    _flowRT[0].texture.wrapS = _flowRT[0].texture.wrapT = THREE.ClampToEdgeWrapping;
    _flowRT[1].texture.wrapS = _flowRT[1].texture.wrapT = THREE.ClampToEdgeWrapping;

    let _flowRead = 0,
      _flowWrite = 1;

    const _flowScene = new THREE.Scene();
    const _flowCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const _flowMat = new THREE.ShaderMaterial({
      uniforms: {
        uFlow: { value: _flowRT[0].texture },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uVel: { value: new THREE.Vector2(0, 0) },
        uDecay: { value: params.flowDecay },
        uInject: { value: params.flowInject }
      },
      vertexShader: mouseVert,
      fragmentShader: mouseFrag
    });
    _flowScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), _flowMat));

    /* ── Merged: Fluid Flow + Lens + Vignette ── */
    const _v3 = new THREE.Vector3();

    const lensPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        uTime: { value: 0 },
        uStrength: { value: params.chromaticAberration ?? 0.003 },
        uVignette: { value: params.vignette },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uVel: { value: new THREE.Vector2(0, 0) },
        uWhalePos: { value: new THREE.Vector2(0.5, 0.5) },
        uWhaleHead: { value: new THREE.Vector2(0.5, 0.5) },
        uWhaleTail: { value: new THREE.Vector2(0.5, 0.5) },
        uFlowMap: { value: _flowRT[0].texture },
        uFlowStrength: { value: params.flowStrength }
      },
      vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader: `
uniform float uTime,uStrength,uVignette,uFlowStrength;
uniform vec2 uMouse,uVel,uWhalePos,uWhaleHead,uWhaleTail;
uniform sampler2D tDiffuse,uFlowMap;
varying vec2 vUv;
void main(){
  vec2 uv=vUv;
  float t=uTime;

  vec2 flow=texture2D(uFlowMap,vUv).rg;

  /* Whale wake */
  vec2 wd=uv-uWhalePos;{float l=length(wd);float inf=exp(-l*7.0);flow+=normalize(wd+0.001).xy*inf*0.02;}
  wd=uv-uWhaleHead;{float l=length(wd);float inf=exp(-l*7.0);flow+=normalize(wd+0.001).xy*inf*0.02;}
  wd=uv-uWhaleTail;{float l=length(wd);float inf=exp(-l*7.0);flow+=normalize(wd+0.001).xy*inf*0.02;}

  /* ── Water lens distortion ── */
  uv.x+=sin(uv.y*8.0+t*0.6)*0.002;
  uv.y+=cos(uv.x*10.0+t*0.5)*0.002;
  uv.x+=sin(uv.x*6.0+uv.y*7.0+t*0.8)*0.0015;
  uv.y+=cos(uv.y*9.0-uv.x*5.0+t*0.7)*0.0015;

  /* Apply flow snake distortion */
  uv+=flow*uFlowStrength;

  /* Chromatic aberration */
  vec2 center=uv-0.5;
  float caDist=length(center);
  float ca=uStrength*(1.0+caDist*1.5);
  vec4 c;
  c.r=texture2D(tDiffuse,uv+vec2(ca,0.0)).r;
  c.g=texture2D(tDiffuse,uv).g;
  c.b=texture2D(tDiffuse,uv-vec2(ca,0.0)).b;
  c.a=1.0;

  /* Vignette */
  vec2 vig=(vUv-0.5)*1.0;
  c.rgb=mix(c.rgb,vec3(1.0-uVignette),dot(vig,vig));

  gl_FragColor=c;
}`
    });
    composer.addPass(lensPass);

    (window as any).FLUID = {
      decay: _flowMat.uniforms.uDecay.value,
      inject: _flowMat.uniforms.uInject.value,
      distort: lensPass.uniforms.uFlowStrength.value
    };
    (window as any).setFluid = (key: string, val: any) => {
      if (key === "decay") {
        _flowMat.uniforms.uDecay.value = val;
        params.flowDecay = val;
      } else if (key === "inject") {
        _flowMat.uniforms.uInject.value = val;
        params.flowInject = val;
      } else if (key === "distort") {
        lensPass.uniforms.uFlowStrength.value = val;
        params.flowStrength = val;
      }
      (window as any).FLUID[key] = val;
    };

    /* ── LUT Color Grading — realistic underwater ── */
    const _lutSize = 32;
    const _lutData = new Uint8Array(_lutSize ** 3 * 4);
    const _lutInv = 1.0 / (_lutSize - 1);
    let _lutIdx = 0;
    for (let b = 0; b < _lutSize; b++) {
      for (let g = 0; g < _lutSize; g++) {
        for (let r = 0; r < _lutSize; r++) {
          let ri = r * _lutInv,
            gi = g * _lutInv,
            bi = b * _lutInv;
          ri *= 0.78;
          gi *= 0.82;
          bi *= 0.88;
          let lum = ri * 0.2126 + gi * 0.7152 + bi * 0.0722;
          ri = ri * 0.85 + lum * 0.15;
          bi = bi * 1.05;
          ri = Math.max(0, Math.min(1, ri));
          gi = Math.max(0, Math.min(1, gi));
          bi = Math.max(0, Math.min(1, bi));
          _lutData[_lutIdx] = Math.round(ri * 255);
          _lutData[_lutIdx + 1] = Math.round(gi * 255);
          _lutData[_lutIdx + 2] = Math.round(bi * 255);
          _lutData[_lutIdx + 3] = 255;
          _lutIdx += 4;
        }
      }
    }
    const _lutTex = new THREE.Data3DTexture(_lutData, _lutSize, _lutSize, _lutSize);
    _lutTex.format = THREE.RGBAFormat;
    _lutTex.type = THREE.UnsignedByteType;
    _lutTex.minFilter = THREE.LinearFilter;
    _lutTex.magFilter = THREE.LinearFilter;
    _lutTex.wrapS = _lutTex.wrapT = _lutTex.wrapR = THREE.ClampToEdgeWrapping;
    _lutTex.needsUpdate = true;

    const lutPass = new LUTPass({ lut: _lutTex, intensity: params.lutIntensity });
    composer.addPass(lutPass);

    composer.addPass(new OutputPass());

    /* ════════════════════════════════════════════════════════════
       WHALE MODEL
       ════════════════════════════════════════════════════════════ */
    let whale: any = null,
      whaleMixer: any = null,
      cameraRig: any = null;
    let useCameraAction = false;
    const whaleCausticShaders: any[] = [];
    let _whaleIdleClip: any = null,
      _whaleMoveClip: any = null,
      _whalePlayClip: any = null;

    const loader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/");
    loader.setDRACOLoader(draco);

    loader.load(
      MODEL_URL,
      (gltf) => {
        whale = gltf.scene;
        whale.traverse((child: any) => {
          if (!child.isMesh) return;
          child.castShadow = true;
          child.receiveShadow = true;

          const mat = child.material;
          if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
            mat.roughness = Math.max(mat.roughness, 0.35);
          }

          const orig = mat.onBeforeCompile;
          mat.onBeforeCompile = (shader: any) => {
            if (orig) orig.call(mat, shader);

            shader.uniforms.uCausticIntensity = { value: params.causticIntensity * 0.15 };
            shader.uniforms.uScale = { value: params.causticScale };
            shader.uniforms.uCausticShadow = { value: params.causticShadow ? 1.0 : 0.0 };
            shader.uniforms.uCausticMap = { value: causticRT.texture };
            shader.uniforms.uWaterHeight = { value: 3.5 };
            shader.uniforms.uTime = { value: 0 };

            shader.vertexShader = shader.vertexShader.replace(
              "#include <common>",
              `#include <common>
varying vec3 vWorldPos;
varying vec3 vWorldNormal;`
            );
            shader.vertexShader = shader.vertexShader.replace(
              "#include <project_vertex>",
              `#include <project_vertex>
vWorldPos=(modelMatrix*vec4(transformed,1.0)).xyz;
vWorldNormal=normalize(mat3(modelMatrix)*objectNormal);`
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              "#include <common>",
              `#include <common>
uniform float uCausticIntensity;
uniform float uScale;
uniform float uCausticShadow;
uniform sampler2D uCausticMap;
uniform float uWaterHeight;
uniform float uTime;
varying vec3 vWorldPos;
varying vec3 vWorldNormal;`
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              "#include <dithering_fragment>",
              `#include <dithering_fragment>
{
  vec2 _cUv=vWorldPos.xz*uScale;
  vec3 _caustic=texture2D(uCausticMap,_cUv).rgb;
  float nFactor=smoothstep(0.0,0.5,vWorldNormal.y);

  #if defined(USE_SHADOWMAP)&&NUM_DIR_LIGHT_SHADOWS>0
    DirectionalLightShadow _dls=directionalLightShadows[0];
    float _shad=getShadow(directionalShadowMap[0],_dls.shadowMapSize,_dls.shadowIntensity,_dls.shadowBias,_dls.shadowRadius,vDirectionalShadowCoord[0]);
    _caustic*=mix(1.0,_shad,uCausticShadow);
  #endif

  gl_FragColor.rgb+=_caustic*uCausticIntensity*nFactor;

  /* ── WATER INTERACTION: foam line + water film ── */
  float _wDist=vWorldPos.y-uWaterHeight;
  float _foam=1.0-smoothstep(0.0,0.15,abs(_wDist));
  _foam*=0.5+0.5*sin(vWorldPos.x*4.0+vWorldPos.z*3.0+uTime*2.0);
  gl_FragColor.rgb+=_foam*vec3(0.7,0.9,1.0)*0.5*nFactor;

  float _film=smoothstep(0.0,0.05,_wDist)*(1.0-smoothstep(0.05,0.6,_wDist));
  _film*=0.5+0.5*sin(vWorldPos.x*6.0+vWorldPos.z*5.0+uTime*3.0);
  vec3 _filmCol=mix(vec3(0.4,0.65,0.85),vec3(0.7,0.85,0.95),_film);
  gl_FragColor.rgb=mix(gl_FragColor.rgb,_filmCol,_film*0.4);
}`
            );
            whaleCausticShaders.push(shader);
          };
          mat.needsUpdate = true;
        });

        // Center whale
        const box = new THREE.Box3().setFromObject(whale);
        const center = box.getCenter(new THREE.Vector3());
        whale.children.forEach((c: any) => c.position.sub(center));

        const whaleGroup = new THREE.Group();
        whaleGroup.add(whale);
        whale.userData.group = whaleGroup;
        whaleGroup.position.set(0, -10, 0);
        scene.add(whaleGroup);

        if (gltf.animations?.length) {
          const clips = gltf.animations;
          console.log(
            "Anim clips found:",
            clips.map((c: any) => c.name)
          );
          whaleMixer = new THREE.AnimationMixer(whale);
          const idleClip = clips.find((c: any) => c.name.toLowerCase().includes("iddle"));
          const moveClip = clips.find((c: any) => c.name.toLowerCase().includes("move"));
          const camClip = clips.find((c: any) => c.name.toLowerCase().includes("camera"));
          console.log("Walk duration:", moveClip!.duration);
          console.log("Idle duration:", idleClip!.duration);
          if (idleClip && moveClip) {
            _whaleIdleClip = idleClip;
            _whaleMoveClip = moveClip;
            let currentAction: any = null;
            let currentClip = moveClip; // mulai dari Walk

            function playClip(clip: any) {
              const nextAction = whaleMixer.clipAction(clip);

              nextAction.reset();
              nextAction.enabled = true;
              nextAction.setLoop(THREE.LoopOnce, 1);
              nextAction.clampWhenFinished = true;
              nextAction.play();

              if (currentAction) {
                currentAction.crossFadeTo(nextAction, 1.0, false);
              }

              currentAction = nextAction;
              currentClip = clip;
            }
            _whalePlayClip = playClip;

            // Mainkan Walk pertama
            playClip(moveClip);

            whaleMixer.addEventListener("finished", (e: any) => {
              if (e.action !== currentAction) return;

              if (currentClip === moveClip) {
                _whalePlayClip(idleClip);
              } else {
                _whalePlayClip(moveClip);
              }
            });
          } else {
            // Fallback: main semua clip bareng
            clips.forEach((c: any) => {
              if (c === camClip) return;
              whaleMixer.clipAction(c).setLoop(THREE.LoopPingPong, Infinity).play();
            });
          }
          if (camClip) {
            const tn = whale.getObjectByName(camClip.tracks[0].name.split(".")[0]);
            if (tn) {
              cameraRig = tn;
              useCameraAction = true;
              const ca = whaleMixer.clipAction(camClip);
              ca.setLoop(THREE.LoopRepeat, Infinity);
              ca.play();
            }
          }

          /* ── Wave ring (ripple from mouth) ── */
          const _waveGeo = new THREE.RingGeometry(0.2, 10, 64);
          const _waveMat = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uDuration: { value: params.waveDuration },
              uMaxRadius: { value: params.waveSize },
              uIntensity: { value: params.waveIntensity },
              uRippleSpeed: { value: params.waveRippleSpeed },
              uCol: { value: new THREE.Color().fromArray(params.waveColor) }
            },
            vertexShader: `
            varying vec2 vUv;
            varying float vRad;
            void main(){
              vUv=uv;
              vRad=length(position.xy);
              gl_Position=projectionMatrix*viewMatrix*modelMatrix*vec4(position,1.0);
            }
          `,
            fragmentShader: `
            uniform float uTime,uDuration,uMaxRadius,uIntensity,uRippleSpeed;
            uniform vec3 uCol;
            varying vec2 vUv;
            varying float vRad;
            void main(){
              float prog=clamp(uTime/uDuration,0.0,1.0);
              float life=1.0-prog;
              float expand=0.3+prog*uMaxRadius;
              float ringW=smoothstep(0.0,0.6,abs(vRad-expand));
              float ring=1.0-ringW;
              float angle=atan(vUv.y-0.5,vUv.x-0.5);
              float ripple=sin(angle*6.0+uTime*uRippleSpeed)*0.35+0.65;
              ring*=ripple;
              float glow=smoothstep(expand-2.0,expand,vRad)-smoothstep(expand,expand+3.0,vRad);
              glow*=0.5;
              float rat=vRad/expand;
              vec3 col=mix(uCol,vec3(0.0,0.4,0.6),rat);
              col+=uCol*ring*0.7;
              float a=(ring+glow)*life*uIntensity;
              if(a<0.01)discard;
              gl_FragColor=vec4(col,a);
            }
          `,
            transparent: true,
            depthWrite: false,
            side: THREE.DoubleSide,
            blending: THREE.AdditiveBlending
          });
          const _waveMesh = new THREE.Mesh(_waveGeo, _waveMat);
          _waveMesh.visible = false;
          _waveMesh.position.set(params.waveOffsetX, params.waveOffsetY, params.waveOffsetZ);
          whaleGroup.add(_waveMesh);
          (window as any)._waveMesh = _waveMesh;
          (window as any)._waveMat = _waveMat;
        }
      },
      undefined,
      (e) => console.error("Gagal load model:", e)
    );

    /* ═══════ CAMERA SHOTS ═══════ */
    function buildShots() {
      const d = params.camDist;
      const h = params.camHeight;
      const tY = params.camTargetY;
      return [
        { pos: new THREE.Vector3(-d * 0.85, h, d * 0.65), target: new THREE.Vector3(0, tY, 0) },
        { pos: new THREE.Vector3(d * 0.75, h - 1, d * 0.75), target: new THREE.Vector3(0, tY, 0) },
        { pos: new THREE.Vector3(0, h + 1, -d * 0.85), target: new THREE.Vector3(0, tY, 0) },
        { pos: new THREE.Vector3(-d * 0.92, h - 2, d * 0.17), target: new THREE.Vector3(0, tY - 0.5, 0) },
        { pos: new THREE.Vector3(-d * 0.67, h, -d * 0.67), target: new THREE.Vector3(0, tY, 0) },
        { pos: new THREE.Vector3(d * 0.17, h - 1, d), target: new THREE.Vector3(0, tY, 0) }
      ];
    }
    let camShots = buildShots();
    let currentShot = 0,
      shotTimer = 0;
    let camPosFrom = camShots[0].pos.clone(),
      camTargetFrom = camShots[0].target.clone();
    let camPosTo = camShots[0].pos.clone(),
      camTargetTo = camShots[0].target.clone();
    let lastInteraction = 0;
    const idleTimeout = 4;
    let cinematicsActive = true,
      cinematicBlend = 0;

    function pickShot(i: number) {
      currentShot = i % camShots.length;
      const next = (currentShot + 1) % camShots.length;
      camPosFrom.copy(camShots[currentShot].pos);
      camTargetFrom.copy(camShots[currentShot].target);
      camPosTo.copy(camShots[next].pos);
      camTargetTo.copy(camShots[next].target);
      shotTimer = 0;
      cinematicBlend = 0;
    }
    let _dcTimer: any = null,
      _dcEnabled = true;
    function onUserInteract() {
      lastInteraction = timer.getElapsed();
      if (cinematicsActive) {
        cinematicsActive = false;
        cinematicBlend = 0;
      }

      // Double-click detection (second click within 350ms) → click.mp3
      if (_dcTimer) {
        clearTimeout(_dcTimer);
        _dcTimer = null;
        if (_dcEnabled && _clickBuffer) {
          playOverlap(_clickBuffer, params.sfxVolume * 0.4);
        }
        /* trigger wave */
        const wm = (window as any)._waveMesh;
        if (wm) {
          wm.visible = true;
          wm.material.uniforms.uTime.value = 0;
        }
        return;
      }
      _dcTimer = setTimeout(() => {
        _dcTimer = null;
      }, 350);

      // Trigger idle only if idle action is NOT currently running
      if (_whaleIdleClip && _whalePlayClip) {
        const idleAction = whaleMixer.clipAction(_whaleIdleClip);
        if (!idleAction.isRunning()) {
          _whalePlayClip(_whaleIdleClip);
        }
      }
    }
    renderer.domElement.addEventListener("pointerdown", onUserInteract);
    renderer.domElement.addEventListener("wheel", onUserInteract);

    /* ═══════ LIGHT UPDATE ═══════ */
    function updateLightPosition(angle: number) {
      const pos = new THREE.Vector3(Math.sin(angle) * lightRadius, 10, Math.cos(angle) * lightRadius);
      sunLight.position.copy(pos);
      sunSphere.position.copy(pos);
      sunDir.copy(pos.clone().normalize());
      if (water.material.uniforms.sunDirection) water.material.uniforms.sunDirection.value.copy(sunDir);
      const incident = sunDir.clone().negate();
      if (floorCausticShader) floorCausticShader.uniforms.uSunDir.value.copy(incident);
    }

    /* ═══════ CUSTOM UI ═══════ */
    (() => {
      const d = document;
      const pnl = root.appendChild(Object.assign(d.createElement("div"), { id: "ui-panel" }));
      createdNodes.push(pnl);

      /* toggle button (SVG gear) */
      const tgl = root.appendChild(d.createElement("button")) as HTMLButtonElement;
      tgl.id = "ui-toggle";
      createdNodes.push(tgl);
      tgl.innerHTML =
        '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';
      let _visible = true;
      tgl.onclick = () => {
        _visible = !_visible;
        pnl.classList.toggle("hidden", !_visible);
      };

      /* header */
      const hd = pnl.appendChild(d.createElement("div"));
      hd.className = "panel-hd";
      hd.innerHTML = '<span class="title">Orca</span><span class="sub">Controls</span>';

      const bd = pnl.appendChild(d.createElement("div"));
      bd.className = "panel-bd";

      /* helpers */
      const _slds: Array<() => void> = [];

      function label(text: string) {
        const e = d.createElement("label");
        e.textContent = text;
        return e;
      }
      function mkSec(title: string) {
        const s = d.createElement("div");
        s.className = "sec";
        const h = s.appendChild(d.createElement("div"));
        h.className = "sec-hd";
        h.innerHTML = `<span class="lbl">${title}</span><span class="arr">▶</span>`;
        const b = s.appendChild(d.createElement("div"));
        b.className = "sec-bd";
        h.onclick = () => s.classList.toggle("open");
        s.classList.add("open");
        bd.appendChild(s);
        return b;
      }
      function mkSub(container: HTMLElement, title: string) {
        const s = container.appendChild(d.createElement("div"));
        s.className = "subsec";
        const h = s.appendChild(d.createElement("div"));
        h.className = "subsec-hd";
        h.innerHTML = `<span class="arr">▶</span>${title}`;
        const b = s.appendChild(d.createElement("div"));
        b.className = "subsec-bd";
        h.onclick = () => s.classList.toggle("open");
        s.classList.add("open");
        return b;
      }
      function sld(key: string, labelText: string, min: number, max: number, step: number, fn?: (v: number) => void) {
        const c = d.createElement("div");
        c.className = "ctrl";
        c.appendChild(label(labelText));
        const r = c.appendChild(d.createElement("div"));
        r.className = "ctrl-row";
        const inp = r.appendChild(d.createElement("input"));
        inp.type = "range";
        inp.min = String(min);
        inp.max = String(max);
        inp.step = String(step);
        inp.value = params[key];
        const v = r.appendChild(d.createElement("span"));
        v.className = "ctrl-val";
        const fmt = (val: any) => {
          const n = Number(val);
          const dd = step < 0.001 ? 3 : step < 0.01 ? 2 : step < 0.1 || step >= 1 ? 1 : step < 0.5 ? 2 : 1;
          return n.toFixed(dd);
        };
        v.textContent = fmt(params[key]);
        inp.oninput = () => {
          const val = parseFloat(inp.value);
          params[key] = val;
          v.textContent = fmt(val);
          fn && fn(val);
        };
        _slds.push(() => {
          inp.value = params[key];
          v.textContent = fmt(params[key]);
        });
        return c;
      }
      function tog(key: string, labelText: string, fn?: (v: boolean) => void) {
        const c = d.createElement("div");
        c.className = "ctrl";
        const l = c.appendChild(d.createElement("label"));
        l.className = "tgl";
        const inp = l.appendChild(d.createElement("input"));
        inp.type = "checkbox";
        inp.checked = params[key];
        l.appendChild(d.createElement("span")).className = "track";
        const lbl = l.appendChild(d.createElement("span"));
        lbl.className = "tgl-lbl";
        lbl.textContent = labelText;
        inp.onchange = () => {
          params[key] = inp.checked;
          fn && fn(inp.checked);
        };
        return c;
      }
      function clr(key: string, labelText: string, fn?: (v: number[]) => void) {
        const c = d.createElement("div");
        c.className = "clr";
        c.appendChild(label(labelText));
        const inp = c.appendChild(d.createElement("input"));
        inp.type = "color";
        const toHex = (a: number[]) =>
          "#" +
          a
            .map((v) =>
              Math.round(v * 255)
                .toString(16)
                .padStart(2, "0")
            )
            .join("");
        const fromHex = (h: string) => [parseInt(h.slice(1, 3), 16) / 255, parseInt(h.slice(3, 5), 16) / 255, parseInt(h.slice(5, 7), 16) / 255];
        inp.value = toHex(params[key]);
        inp.oninput = () => {
          const arr = fromHex(inp.value);
          params[key] = arr;
          fn && fn(arr);
        };
        return c;
      }
      function btn(text: string, fn: () => void, cls?: string) {
        const b = d.createElement("button");
        b.className = "ui-btn" + (cls ? " " + cls : "");
        b.textContent = text;
        b.onclick = fn;
        return b;
      }

      /* ── WATER ── */
      const w = mkSec("Water");
      w.appendChild(
        sld("waterOpacity", "Opacity", 0.1, 1, 0.01, (v) => {
          if (water.material.uniforms.alpha) water.material.uniforms.alpha.value = v;
        })
      );
      w.appendChild(sld("waveSpeed", "Wave Speed", 0.1, 3, 0.05));
      w.appendChild(
        sld("waveHeight", "Wave Height", 0.1, 3, 0.05, (v) => {
          if (water.material.uniforms.distortionScale) water.material.uniforms.distortionScale.value = v * 12;
        })
      );

      /* ── CAUSTICS ── */
      const c = mkSec("Caustics");
      c.appendChild(
        sld("causticIntensity", "Intensity", 0, 5, 0.05, (v) => {
          if (floorCausticShader) floorCausticShader.uniforms.uCausticIntensity.value = v;
          whaleCausticShaders.forEach((s) => {
            if (s.uniforms.uCausticIntensity) s.uniforms.uCausticIntensity.value = v * 0.15;
          });
        })
      );
      c.appendChild(sld("causticSpeed", "Speed", 0, 2, 0.01));
      c.appendChild(
        sld("causticScale", "Proj Scale", 0.01, 2, 0.01, (v) => {
          if (floorCausticShader) floorCausticShader.uniforms.uScale.value = v;
          whaleCausticShaders.forEach((s) => {
            if (s.uniforms.uScale) s.uniforms.uScale.value = v;
          });
        })
      );
      c.appendChild(
        tog("causticShadow", "Shadow", (v) => {
          const f = v ? 1 : 0;
          if (floorCausticShader) floorCausticShader.uniforms.uCausticShadow.value = f;
          whaleCausticShaders.forEach((s) => {
            if (s.uniforms.uCausticShadow) s.uniforms.uCausticShadow.value = f;
          });
        })
      );

      const l1 = mkSub(c, "Layer 1");
      l1.appendChild(
        sld("causticL1Scale", "Scale", 0.5, 20, 0.1, (v) => {
          causticRTMat.uniforms.uL1Scale.value = v;
        })
      );
      l1.appendChild(
        sld("causticL1Speed", "Speed", 0, 3, 0.01, (v) => {
          causticRTMat.uniforms.uL1Speed.value = v;
        })
      );
      l1.appendChild(
        sld("causticL1Weight", "Weight", 0, 1, 0.01, (v) => {
          causticRTMat.uniforms.uL1Weight.value = v;
        })
      );
      l1.appendChild(
        sld("causticL1Exp", "Exponent", 0.1, 5, 0.05, (v) => {
          causticRTMat.uniforms.uL1Exp.value = v;
        })
      );

      const l2 = mkSub(c, "Layer 2");
      l2.appendChild(
        sld("causticL2Scale", "Scale", 0.5, 20, 0.1, (v) => {
          causticRTMat.uniforms.uL2Scale.value = v;
        })
      );
      l2.appendChild(
        sld("causticL2Speed", "Speed", 0, 3, 0.01, (v) => {
          causticRTMat.uniforms.uL2Speed.value = v;
        })
      );
      l2.appendChild(
        sld("causticL2Weight", "Weight", 0, 1, 0.01, (v) => {
          causticRTMat.uniforms.uL2Weight.value = v;
        })
      );
      l2.appendChild(
        sld("causticL2Exp", "Exponent", 0.1, 5, 0.05, (v) => {
          causticRTMat.uniforms.uL2Exp.value = v;
        })
      );

      const l3 = mkSub(c, "Layer 3");
      l3.appendChild(
        sld("causticL3Scale", "Scale", 0.5, 20, 0.1, (v) => {
          causticRTMat.uniforms.uL3Scale.value = v;
        })
      );
      l3.appendChild(
        sld("causticL3Speed", "Speed", 0, 3, 0.01, (v) => {
          causticRTMat.uniforms.uL3Speed.value = v;
        })
      );
      l3.appendChild(
        sld("causticL3Weight", "Weight", 0, 1, 0.01, (v) => {
          causticRTMat.uniforms.uL3Weight.value = v;
        })
      );
      l3.appendChild(
        sld("causticL3Exp", "Exponent", 0.1, 5, 0.05, (v) => {
          causticRTMat.uniforms.uL3Exp.value = v;
        })
      );

      const dr = mkSub(c, "Drift");
      dr.appendChild(
        sld("causticDrift1X", "Drift 1 X", -0.1, 0.1, 0.001, (v) => {
          causticRTMat.uniforms.uDrift1X.value = v;
        })
      );
      dr.appendChild(
        sld("causticDrift1Y", "Drift 1 Y", -0.1, 0.1, 0.001, (v) => {
          causticRTMat.uniforms.uDrift1Y.value = v;
        })
      );
      dr.appendChild(
        sld("causticDrift2X", "Drift 2 X", -0.1, 0.1, 0.001, (v) => {
          causticRTMat.uniforms.uDrift2X.value = v;
        })
      );
      dr.appendChild(
        sld("causticDrift2Y", "Drift 2 Y", -0.1, 0.1, 0.001, (v) => {
          causticRTMat.uniforms.uDrift2Y.value = v;
        })
      );

      const cl = mkSub(c, "Cell");
      cl.appendChild(
        sld("causticEdgeThresh", "Edge Thresh", 0, 1, 0.01, (v) => {
          causticRTMat.uniforms.uEdgeThresh.value = v;
        })
      );
      cl.appendChild(
        sld("causticCenterSup", "Center Sup", 0, 0.5, 0.01, (v) => {
          causticRTMat.uniforms.uCenterSup.value = v;
        })
      );
      cl.appendChild(
        sld("causticCellSpeed", "Anim Speed", 0, 3, 0.01, (v) => {
          causticRTMat.uniforms.uCellAnimSpeed.value = v;
        })
      );

      const gw = mkSub(c, "Glow");
      gw.appendChild(
        sld("causticGlow1W", "Glow 1", 0, 1, 0.01, (v) => {
          causticRTMat.uniforms.uGlow1W.value = v;
        })
      );
      gw.appendChild(
        sld("causticGlow2W", "Glow 2", 0, 1, 0.01, (v) => {
          causticRTMat.uniforms.uGlow2W.value = v;
        })
      );

      const co = mkSub(c, "Color");
      co.appendChild(
        clr("causticColor1", "Base", (v) => {
          causticRTMat.uniforms.uCol1.value.fromArray(v);
        })
      );
      co.appendChild(
        clr("causticColor2", "Highlight", (v) => {
          causticRTMat.uniforms.uCol2.value.fromArray(v);
        })
      );

      /* ── POST-PROCESSING ── */
      const pp = mkSec("Post-Processing");
      pp.appendChild(
        tog("refraction", "Refraction", (v) => {
          refractionPass.enabled = v;
        })
      );
      pp.appendChild(
        sld("refractionStrength", "Refract Strength", 0, 0.04, 0.001, (v) => {
          refractionPass.uniforms.uStrength.value = v;
        })
      );
      pp.appendChild(
        sld("refractionSpeed", "Refract Speed", 0, 3, 0.05, (v) => {
          refractionPass.uniforms.uSpeed.value = v;
        })
      );
      pp.appendChild(
        sld("bloomIntensity", "Bloom", 0, 1, 0.01, (v) => {
          bloomPass.strength = v;
        })
      );
      pp.appendChild(
        sld("vignette", "Vignette", 0, 1, 0.01, (v) => {
          lensPass.uniforms.uVignette.value = v;
        })
      );
      pp.appendChild(
        sld("chromaticAberration", "Chromatic Aberr", 0, 0.02, 0.0005, (v) => {
          lensPass.uniforms.uStrength.value = v;
        })
      );
      pp.appendChild(
        sld("lutIntensity", "LUT Grade", 0, 1, 0.01, (v) => {
          lutPass.intensity = v;
        })
      );

      /* ── FLUID SIM ── */
      const fm = mkSec("Fluid Sim");
      fm.appendChild(
        sld("flowStrength", "Distortion", 0, 0.2, 0.001, (v) => {
          lensPass.uniforms.uFlowStrength.value = v;
        })
      );
      fm.appendChild(
        sld("flowDecay", "Decay", 0.8, 0.995, 0.005, (v) => {
          _flowMat.uniforms.uDecay.value = v;
        })
      );
      fm.appendChild(
        sld("flowInject", "Inject", 0, 10, 0.1, (v) => {
          _flowMat.uniforms.uInject.value = v;
        })
      );

      /* ── SCENE ── */
      const sc = mkSec("Scene");
      sc.appendChild(
        tog("godRays", "God Rays", (v) => {
          godRayPass.enabled = v;
        })
      );
      sc.appendChild(
        sld("godRayIntensity", "Ray Intensity", 0, 3, 0.05, (v) => {
          godRayPass.uniforms.uIntensity.value = v;
        })
      );
      sc.appendChild(
        sld("godRayDecay", "Decay", 0.8, 1, 0.005, (v) => {
          godRayPass.uniforms.uDecay.value = v;
        })
      );
      sc.appendChild(
        sld("godRaySamples", "Samples", 8, 128, 1, (v) => {
          godRayPass.uniforms.uSamples.value = v;
        })
      );
      sc.appendChild(sld("particleDensity", "Particle Density", 0, 2, 0.1));
      sc.appendChild(sld("orcaSpeed", "Orca Speed", 0.1, 1, 0.01));
      sc.appendChild(sld("camTransitionSpeed", "Cam Speed", 1, 8, 0.1));
      sc.appendChild(
        tog("autoRotate", "Cinematic Cam", (v) => {
          if (v) {
            pickShot(currentShot);
            cinematicBlend = 1;
            cinematicsActive = true;
          } else {
            cinematicsActive = false;
          }
        })
      );
      sc.appendChild(sld("lightLoopSpeed", "Light Loop Speed", 0, 0.2, 0.005));
      sc.appendChild(
        sld("lightAngle", "Light Angle", -Math.PI, Math.PI, 0.01, (v) => {
          updateLightPosition(v);
        })
      );
      sc.appendChild(
        tog("waveEnabled", "Wave Ring", (v) => {
          const w = (window as any)._waveMesh;
          if (w && !v) w.visible = false;
        })
      );
      sc.appendChild(
        sld("waveDuration", "Wave Duration", 0.2, 2, 0.05, (v) => {
          const u = (window as any)._waveMat?.uniforms;
          if (u) u.uDuration.value = v;
        })
      );
      sc.appendChild(
        sld("waveSize", "Wave Size", 1, 15, 0.5, (v) => {
          const u = (window as any)._waveMat?.uniforms;
          if (u) u.uMaxRadius.value = v;
        })
      );
      sc.appendChild(
        sld("waveIntensity", "Wave Intensity", 0, 2, 0.05, (v) => {
          const u = (window as any)._waveMat?.uniforms;
          if (u) u.uIntensity.value = v;
        })
      );
      sc.appendChild(
        sld("waveRippleSpeed", "Ripple Speed", 0, 40, 1, (v) => {
          const u = (window as any)._waveMat?.uniforms;
          if (u) u.uRippleSpeed.value = v;
        })
      );
      sc.appendChild(
        clr("waveColor", "Wave Color", (v) => {
          const u = (window as any)._waveMat?.uniforms;
          if (u) u.uCol.value.fromArray(v);
        })
      );
      const updPos = () => {
        const w = (window as any)._waveMesh;
        if (w) w.position.set(params.waveOffsetX, params.waveOffsetY, params.waveOffsetZ);
      };
      sc.appendChild(sld("waveOffsetX", "Wave Pos X", -10, 10, 0.05, () => updPos()));
      sc.appendChild(sld("waveOffsetY", "Wave Pos Y", -10, 10, 0.05, () => updPos()));
      sc.appendChild(sld("waveOffsetZ", "Wave Pos Z", -10, 10, 0.05, () => updPos()));

      const camApply = () => {
        camShots = buildShots();
        pickShot(currentShot);
        camera.position.copy(camShots[0].pos);
        controls.target.copy(camShots[0].target);
      };
      sc.appendChild(
        sld("camHeight", "Cam Height", 1, 12, 0.1, () => {
          camApply();
        })
      );
      sc.appendChild(
        sld("camDist", "Cam Distance", 5, 25, 0.5, () => {
          camApply();
        })
      );
      sc.appendChild(
        sld("camTargetY", "Cam Target Y", -12, 6, 0.1, () => {
          camApply();
        })
      );

      /* ── AUDIO ── */
      const au = mkSec("Audio");
      au.appendChild(
        sld("bgmVolume", "BGM Volume", 0, 1, 0.01, (v) => {
          bgm.setVolume(v);
        })
      );
      au.appendChild(
        sld("waterVolume", "Water Volume", 0, 1, 0.01, (v) => {
          waterAmbient.setVolume(v);
        })
      );
      au.appendChild(
        sld("sfxVolume", "SFX Volume", 0, 1, 0.01, (v) => {
          orcaAudio.setVolume(v * 0.7);
          mouseAudio.setVolume(v * 0.4);
        })
      );

      /* ── Reset + Config ── */
      const rb = bd.appendChild(d.createElement("div"));
      rb.className = "ui-btn-row";
      rb.appendChild(
        btn(
          "Reset Defaults",
          () => {
            Object.assign(params, defaults);
            params.causticColor1 = [...defaults.causticColor1];
            params.causticColor2 = [...defaults.causticColor2];
            if (water.material.uniforms.alpha) water.material.uniforms.alpha.value = defaults.waterOpacity;
            if (water.material.uniforms.time) water.material.uniforms.time.value = 0;
            if (water.material.uniforms.distortionScale) water.material.uniforms.distortionScale.value = defaults.waveHeight * 15;
            causticRTMat.uniforms.uSpeed.value = defaults.causticSpeed;
            causticRTMat.uniforms.uL1Scale.value = defaults.causticL1Scale;
            causticRTMat.uniforms.uL1Speed.value = defaults.causticL1Speed;
            causticRTMat.uniforms.uL1Weight.value = defaults.causticL1Weight;
            causticRTMat.uniforms.uL1Exp.value = defaults.causticL1Exp;
            causticRTMat.uniforms.uL2Scale.value = defaults.causticL2Scale;
            causticRTMat.uniforms.uL2Speed.value = defaults.causticL2Speed;
            causticRTMat.uniforms.uL2Weight.value = defaults.causticL2Weight;
            causticRTMat.uniforms.uL2Exp.value = defaults.causticL2Exp;
            causticRTMat.uniforms.uL3Scale.value = defaults.causticL3Scale;
            causticRTMat.uniforms.uL3Speed.value = defaults.causticL3Speed;
            causticRTMat.uniforms.uL3Weight.value = defaults.causticL3Weight;
            causticRTMat.uniforms.uL3Exp.value = defaults.causticL3Exp;
            causticRTMat.uniforms.uDrift1X.value = defaults.causticDrift1X;
            causticRTMat.uniforms.uDrift1Y.value = defaults.causticDrift1Y;
            causticRTMat.uniforms.uDrift2X.value = defaults.causticDrift2X;
            causticRTMat.uniforms.uDrift2Y.value = defaults.causticDrift2Y;
            causticRTMat.uniforms.uEdgeThresh.value = defaults.causticEdgeThresh;
            causticRTMat.uniforms.uCenterSup.value = defaults.causticCenterSup;
            causticRTMat.uniforms.uCellAnimSpeed.value = defaults.causticCellSpeed;
            causticRTMat.uniforms.uGlow1W.value = defaults.causticGlow1W;
            causticRTMat.uniforms.uGlow2W.value = defaults.causticGlow2W;
            causticRTMat.uniforms.uCol1.value.fromArray(defaults.causticColor1);
            causticRTMat.uniforms.uCol2.value.fromArray(defaults.causticColor2);
            if (floorCausticShader) {
              floorCausticShader.uniforms.uCausticIntensity.value = defaults.causticIntensity;
              floorCausticShader.uniforms.uScale.value = defaults.causticScale;
              floorCausticShader.uniforms.uCausticShadow.value = defaults.causticShadow ? 1 : 0;
            }
            whaleCausticShaders.forEach((s) => {
              if (s.uniforms.uCausticIntensity) s.uniforms.uCausticIntensity.value = defaults.causticIntensity * 0.15;
              if (s.uniforms.uScale) s.uniforms.uScale.value = defaults.causticScale;
              if (s.uniforms.uCausticShadow) s.uniforms.uCausticShadow.value = defaults.causticShadow ? 1 : 0;
            });
            causticRTMat.uniforms.uIntensity.value = defaults.causticIntensity;
            refractionPass.enabled = defaults.refraction;
            refractionPass.uniforms.uStrength.value = defaults.refractionStrength;
            refractionPass.uniforms.uSpeed.value = defaults.refractionSpeed;
            bloomPass.strength = defaults.bloomIntensity;
            godRayPass.enabled = defaults.godRays;
            godRayPass.uniforms.uIntensity.value = defaults.godRayIntensity;
            godRayPass.uniforms.uDecay.value = defaults.godRayDecay;
            godRayPass.uniforms.uSamples.value = defaults.godRaySamples;
            lensPass.uniforms.uStrength.value = defaults.chromaticAberration;
            lensPass.uniforms.uVignette.value = defaults.vignette;
            lensPass.uniforms.uFlowStrength.value = defaults.flowStrength;
            _flowMat.uniforms.uDecay.value = defaults.flowDecay;
            _flowMat.uniforms.uInject.value = defaults.flowInject;
            lutPass.intensity = defaults.lutIntensity;
            pickShot(currentShot);
            cinematicBlend = 1;
            cinematicsActive = true;
            updateLightPosition(defaults.lightAngle);
            camShots = buildShots();
            const wu = (window as any)._waveMat?.uniforms;
            if (wu) {
              wu.uDuration.value = defaults.waveDuration;
              wu.uMaxRadius.value = defaults.waveSize;
              wu.uIntensity.value = defaults.waveIntensity;
              wu.uRippleSpeed.value = defaults.waveRippleSpeed;
              wu.uCol.value.fromArray(defaults.waveColor);
            }
            updPos();
            _slds.forEach((f) => f());
          },
          "primary"
        )
      );

      (window as any)._uiUpdate = _slds;
    })();

    /* ═══════ HUD ═══════ */
    const hud = root.appendChild(document.createElement("div"));
    hud.id = "hud";
    createdNodes.push(hud);
    function hudBtn(ico: string, label: string, init: boolean, fn: (on: boolean) => void) {
      const b = hud.appendChild(document.createElement("button"));
      b.className = "hud-btn" + (init ? " on" : "");
      b.innerHTML = `<span class="ico">${ico}</span><span class="dot ${init ? "on" : "off"}"></span><span class="tip">${label}</span>`;
      let on = init;
      b.onclick = () => {
        on = !on;
        b.className = "hud-btn" + (on ? " on" : "");
        b.querySelector(".dot")!.className = "dot " + (on ? "on" : "off");
        fn(on);
      };
      return b;
    }
    hudBtn("♫", "Music", true, (on) => {
      if (!_audioLoaded.bgm) return;
      on ? bgm.play() : bgm.pause();
    });
    hudBtn("⇉", "Double-Click Sound", true, (on) => {
      _dcEnabled = on;
    });
    hudBtn("⊡", "Cinematic Cam", params.autoRotate, (on) => {
      params.autoRotate = on;
      if (on) {
        pickShot(currentShot);
        cinematicBlend = 1;
        cinematicsActive = true;
      } else cinematicsActive = false;
    });
    hudBtn("▤", "SFX", true, (on) => {
      params.sfxVolume = on ? defaults.sfxVolume : 0;
      orcaAudio.setVolume((on ? defaults.sfxVolume : 0) * 0.7);
      mouseAudio.setVolume((on ? defaults.sfxVolume : 0) * 0.4);
      (window as any)._uiUpdate.forEach((f: () => void) => f());
    });

    /* ═══════ CONFIG SAVE/LOAD ═══════ */
    const configInput = document.createElement("input");
    configInput.type = "file";
    configInput.accept = ".json";
    configInput.style.display = "none";
    root.appendChild(configInput);
    createdNodes.push(configInput);
    configInput.addEventListener("change", () => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target!.result as string);
          Object.assign(params, data);
          // Apply all uniform changes
          if (water.material.uniforms.alpha) water.material.uniforms.alpha.value = params.waterOpacity;
          if (water.material.uniforms.time) water.material.uniforms.time.value = 0;
          causticRTMat.uniforms.uSpeed.value = params.causticSpeed;
          causticRTMat.uniforms.uL1Scale.value = params.causticL1Scale;
          causticRTMat.uniforms.uL1Speed.value = params.causticL1Speed;
          causticRTMat.uniforms.uL1Weight.value = params.causticL1Weight;
          causticRTMat.uniforms.uL1Exp.value = params.causticL1Exp;
          causticRTMat.uniforms.uL2Scale.value = params.causticL2Scale;
          causticRTMat.uniforms.uL2Speed.value = params.causticL2Speed;
          causticRTMat.uniforms.uL2Weight.value = params.causticL2Weight;
          causticRTMat.uniforms.uL2Exp.value = params.causticL2Exp;
          causticRTMat.uniforms.uL3Scale.value = params.causticL3Scale;
          causticRTMat.uniforms.uL3Speed.value = params.causticL3Speed;
          causticRTMat.uniforms.uL3Weight.value = params.causticL3Weight;
          causticRTMat.uniforms.uL3Exp.value = params.causticL3Exp;
          causticRTMat.uniforms.uDrift1X.value = params.causticDrift1X;
          causticRTMat.uniforms.uDrift1Y.value = params.causticDrift1Y;
          causticRTMat.uniforms.uDrift2X.value = params.causticDrift2X;
          causticRTMat.uniforms.uDrift2Y.value = params.causticDrift2Y;
          causticRTMat.uniforms.uEdgeThresh.value = params.causticEdgeThresh;
          causticRTMat.uniforms.uCenterSup.value = params.causticCenterSup;
          causticRTMat.uniforms.uCellAnimSpeed.value = params.causticCellSpeed;
          causticRTMat.uniforms.uGlow1W.value = params.causticGlow1W;
          causticRTMat.uniforms.uGlow2W.value = params.causticGlow2W;
          causticRTMat.uniforms.uCol1.value.fromArray(params.causticColor1);
          causticRTMat.uniforms.uCol2.value.fromArray(params.causticColor2);
          if (floorCausticShader) floorCausticShader.uniforms.uCausticIntensity.value = params.causticIntensity;
          whaleCausticShaders.forEach((s) => {
            if (s.uniforms.uCausticIntensity) s.uniforms.uCausticIntensity.value = params.causticIntensity * 0.15;
          });
          causticRTMat.uniforms.uIntensity.value = params.causticIntensity;
          refractionPass.uniforms.uStrength.value = params.refractionStrength;
          refractionPass.uniforms.uSpeed.value = params.refractionSpeed;
          refractionPass.enabled = params.refraction;
          bloomPass.strength = params.bloomIntensity;
          godRayPass.enabled = params.godRays;
          godRayPass.uniforms.uIntensity.value = params.godRayIntensity;
          godRayPass.uniforms.uDecay.value = params.godRayDecay;
          godRayPass.uniforms.uSamples.value = params.godRaySamples;
          lensPass.uniforms.uStrength.value = params.chromaticAberration;
          lensPass.uniforms.uVignette.value = params.vignette;
          lensPass.uniforms.uFlowStrength.value = params.flowStrength;
          _flowMat.uniforms.uDecay.value = params.flowDecay;
          _flowMat.uniforms.uInject.value = params.flowInject;
          lutPass.intensity = params.lutIntensity;
          updateLightPosition(params.lightAngle);
          (window as any)._uiUpdate.forEach((f: () => void) => f());
        } catch (err) {
          console.error("Config load error:", err);
        }
      };
      reader.readAsText(configInput.files![0]);
      configInput.value = "";
    });
    const configActions = {
      saveConfig: () => {
        const blob = new Blob([JSON.stringify(params, null, 2)], { type: "application/json" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "orca-config.json";
        a.click();
        URL.revokeObjectURL(a.href);
      },
      saveAsModule: () => {
        const out = `export const defaults = ${JSON.stringify(params, null, 2)};\n`;
        const blob = new Blob([out], { type: "text/javascript" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "config.js";
        a.click();
        URL.revokeObjectURL(a.href);
      },
      loadConfig: () => configInput.click()
    };
    const fb = document.getElementById("ui-panel")!.querySelector(".panel-bd")!;
    const br = fb.appendChild(document.createElement("div"));
    br.className = "ui-btn-row";
    br.appendChild(Object.assign(document.createElement("button"), { className: "ui-btn", textContent: "Save Config", onclick: configActions.saveConfig }));
    br.appendChild(Object.assign(document.createElement("button"), { className: "ui-btn", textContent: "Load Config", onclick: configActions.loadConfig }));
    br.appendChild(Object.assign(document.createElement("button"), { className: "ui-btn", textContent: "Export JS", onclick: configActions.saveAsModule }));

    /* ═══════ TIMER & INIT ═══════ */
    const timer = new THREE.Timer();
    timer.connect(document);
    camera.position.copy(camShots[0].pos);
    controls.target.copy(camShots[0].target);
    pickShot(0);

    /* ═══════ ANIMATION LOOP ═══════ */
    const _rollQ = new THREE.Quaternion();
    const _rollAxis = new THREE.Vector3(0, 0, 1);
    const _fwdDir = new THREE.Vector3(0, 0, 1);

    /* Reusable vectors for perf (no GC) */
    const _vA = new THREE.Vector3(),
      _vB = new THREE.Vector3(),
      _vC = new THREE.Vector3();
    const _vD = new THREE.Vector3(),
      _vE = new THREE.Vector3(),
      _vF = new THREE.Vector3();
    const _vG = new THREE.Vector3(),
      _vH = new THREE.Vector3(),
      _vI = new THREE.Vector3();
    const _qA = new THREE.Quaternion();

    let rafId = 0;
    function animate() {
      rafId = requestAnimationFrame(animate);
      timer.update();
      const elapsed = timer.getElapsed();
      const delta = Math.min(timer.getDelta(), 0.05);

      underCurrentPass.uniforms.uTime.value = elapsed;
      lensPass.uniforms.uTime.value = elapsed;
      const vx = _mouse.x - _mouse.px,
        vy = _mouse.y - _mouse.py;
      lensPass.uniforms.uMouse.value.set(_mouse.x, _mouse.y);
      lensPass.uniforms.uVel.value.set(vx, vy);

      // Water time — track untuk sinkronisasi god ray
      if (water.material.uniforms.time) water.material.uniforms.time.value += delta * params.waveSpeed;
      const waterTime = water.material.uniforms.time.value;

      // Caustic pre-render
      causticRTMat.uniforms.uTime.value = elapsed;
      causticRTMat.uniforms.uSpeed.value = params.causticSpeed;
      whaleCausticShaders.forEach((s) => {
        if (s.uniforms.uTime) s.uniforms.uTime.value = elapsed;
      });
      renderer.setRenderTarget(causticRT);
      renderer.render(causticRTScene, causticRTCam);
      renderer.setRenderTarget(null);

      // ── Fluid sim (ping-pong) ──
      {
        _flowMat.uniforms.uMouse.value.set(_mouse.x, _mouse.y);
        _flowMat.uniforms.uVel.value.set(vx, vy);
        _flowMat.uniforms.uFlow.value = _flowRT[_flowRead].texture;
        renderer.setRenderTarget(_flowRT[_flowWrite]);
        renderer.render(_flowScene, _flowCam);
        renderer.setRenderTarget(null);
        // Swap
        const tmp = _flowRead;
        _flowRead = _flowWrite;
        _flowWrite = tmp;
        // Update lens pass
        lensPass.uniforms.uFlowMap.value = _flowRT[_flowRead].texture;
      }

      (window as any).FLUID.decay = _flowMat.uniforms.uDecay.value;
      (window as any).FLUID.inject = _flowMat.uniforms.uInject.value;
      (window as any).FLUID.distort = lensPass.uniforms.uFlowStrength.value;

      if (refractionPass) refractionPass.uniforms.uTime.value = elapsed;

      // ── God ray: update sun screen pos + sinkron water time ──
      if (godRayPass.enabled) {
        _godRayVec.set(sunDir.x * 50, 3.5, sunDir.z * 50).project(camera);
        let sx = _godRayVec.x * 0.5 + 0.5;
        let sy = _godRayVec.y * -0.5 + 0.5;
        // Sun di belakang kamera → fallback ke atas tengah
        if (_godRayVec.z > 1.0) {
          sx = 0.5;
          sy = 1.05;
        }
        godRaySunScreen.set(THREE.MathUtils.clamp(sx, -0.15, 1.15), THREE.MathUtils.clamp(sy, -0.15, 1.15));
        // SINKRON: pakai water time yang sama dengan Water.js
        godRayPass.uniforms.uWaterTime.value = waterTime;
      }

      // ── LIGHT LOOP ──
      updateLightPosition(params.lightAngle + elapsed * params.lightLoopSpeed);

      // ── WHALE MOVEMENT ──
      if (whale) {
        const group = whale.userData.group;
        if (whaleMixer) whaleMixer.update(Math.min(delta, 0.05) * params.orcaSpeed);
      }

      // ── WAVE (shockwave from mouth) ──
      if (whale) {
        const wm = (window as any)._waveMesh;
        if (wm && wm.visible) {
          wm.material.uniforms.uTime.value += delta;
          if (wm.material.uniforms.uTime.value >= wm.material.uniforms.uDuration.value) {
            wm.visible = false;
          }
        }
      }

      // ── WHALE WAKE — no per-frame allocs ──
      if (whale) {
        const _g = whale.userData.group;
        whale.getWorldPosition(_v3);
        _v3.project(camera);
        lensPass.uniforms.uWhalePos.value.set(_v3.x * 0.5 + 0.5, _v3.y * -0.5 + 0.5);
        if (_g) {
          const q = _g.quaternion;
          _vA.set(0, 0, 1).applyQuaternion(q).multiplyScalar(1.8).add(_g.position);
          _vB.set(0, 0, -1).applyQuaternion(q).multiplyScalar(1.8).add(_g.position);
          _vA.project(camera);
          _vB.project(camera);
          lensPass.uniforms.uWhaleHead.value.set(_vA.x * 0.5 + 0.5, _vA.y * -0.5 + 0.5);
          lensPass.uniforms.uWhaleTail.value.set(_vB.x * 0.5 + 0.5, _vB.y * -0.5 + 0.5);
        }
      }

      // ── ORCA SOUND (periodic, rare ambient) ──
      if (whale && _audioLoaded.orca && !orcaAudio.isPlaying && elapsed % 60 < delta) {
        orcaAudio.setVolume(params.sfxVolume * 0.5);
        orcaAudio.play();
      }

      // ── CINEMATIC CAMERA ──
      if (whale && params.autoRotate) {
        if (!cinematicsActive && elapsed - lastInteraction > idleTimeout) {
          cinematicsActive = true;
          pickShot(currentShot);
        }
        if (cinematicsActive) {
          if (useCameraAction && cameraRig) {
            cinematicBlend = Math.min(cinematicBlend + delta * 2, 1);
            cameraRig.getWorldPosition(_vE);
            cameraRig.getWorldQuaternion(_qA);
            if (cinematicBlend < 1) {
              camera.position.lerp(_vE, cinematicBlend);
              camera.quaternion.slerp(_qA, cinematicBlend);
            } else {
              camera.position.copy(_vE);
              camera.quaternion.copy(_qA);
            }
            whale.getWorldPosition(_vF);
            controls.target.lerp(_vF, 0.05);
          } else {
            shotTimer += delta;
            if (shotTimer > params.camTransitionSpeed) pickShot((currentShot + 1) % camShots.length);
            const st = ((t: number) => t * t * (3 - 2 * t))(Math.min(shotTimer / params.camTransitionSpeed, 1));
            cinematicBlend = Math.min(cinematicBlend + delta * 2, 1);
            const group = whale.userData.group;
            _vG.copy(group ? group.position : _vG.set(0, 0, 0));
            _vH.copy(camPosFrom).add(_vG);
            _vI.copy(camPosTo).add(_vG);
            _vA.lerpVectors(_vH, _vI, st);
            _vB.lerpVectors(camTargetFrom, camTargetTo, st).add(_vG);
            if (cinematicBlend < 1) {
              camera.position.lerpVectors(camera.position, _vA, cinematicBlend);
              controls.target.lerpVectors(controls.target, _vB, cinematicBlend);
            } else {
              camera.position.copy(_vA);
              controls.target.copy(_vB);
            }
          }
        }
      }

      // ── BUBBLES ──
      const bA = bubbles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < bubbleCount; i++) {
        bA[i * 3 + 1] += bSpd[i] * 0.012;
        bA[i * 3] += Math.sin(elapsed * bSpd[i] + bOff[i]) * 0.003;
        bA[i * 3 + 2] += Math.cos(elapsed * bSpd[i] * 0.8 + bOff[i]) * 0.003;
        if (bA[i * 3 + 1] > 3.5) {
          bA[i * 3 + 1] = -5;
          bA[i * 3] = (Math.random() - 0.5) * 45;
          bA[i * 3 + 2] = (Math.random() - 0.5) * 45;
        }
      }
      bubbles.geometry.attributes.position.needsUpdate = true;

      // ── DEBRIS ──
      const dA = debris.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < debrisCount; i++) {
        dA[i * 3] += Math.sin(elapsed * dSpd[i] + dOff[i]) * 0.002;
        dA[i * 3 + 1] += Math.cos(elapsed * dSpd[i] * 0.7 + dOff[i]) * 0.001;
        dA[i * 3 + 2] += Math.cos(elapsed * dSpd[i] + dOff[i] * 1.3) * 0.002;
      }
      debris.geometry.attributes.position.needsUpdate = true;

      // ── FISH ──
      for (let i = 0; i < fishCount; i++) {
        const d = fishD[i];
        d.a += d.s * 0.005;
        const x = Math.cos(d.a) * d.r,
          z = Math.sin(d.a) * d.r;
        const y = d.h + Math.sin(elapsed * d.s * 0.5 + d.p) * 0.3;
        _dum.position.set(x, y, z);
        _dum.lookAt(0, y, 0);
        _dum.updateMatrix();
        fishMesh.setMatrixAt(i, _dum.matrix);
      }
      fishMesh.instanceMatrix.needsUpdate = true;

      controls.update();
      composer.render();
    }
    animate();

    /* ═══════ RESIZE ═══════ */
    const _onResize = () => {
      const w = innerWidth,
        h = innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
    };
    addEventListener("resize", _onResize);

    return () => {
      cancelAnimationFrame(rafId);
      removeEventListener("resize", _onResize);
      removeEventListener("mousemove", _onMouseMove);
      document.removeEventListener("pointerdown", _resumePointer);
      document.removeEventListener("keydown", _resumeKey);
      [bgm, waterAmbient, orcaAudio, mouseAudio, clickAudio].forEach((a) => {
        if (a.isPlaying) a.stop();
      });
      renderer.dispose();
      createdNodes.forEach((n) => n.remove());
      (window as any)._uiUpdate = null;
      (window as any)._waveMesh = null;
      (window as any)._waveMat = null;
      (window as any).setFluid = null;
      console.warn = _origWarn;
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        margin: 0,
        padding: 0,
        background: "#081828",
      }}
    >
      <div id='info'>ORCA</div>
      <style>{`
        *{margin:0;padding:0;box-sizing:border-box}
        body{overflow:hidden;background:#000;font-family:'Inter','Segoe UI','Helvetica Neue',sans-serif;color:rgba(255,255,255,0.82)}
        canvas{display:block}
        #info{position:fixed;top:40px;left:50%;transform:translateX(-50%);color:rgba(255,255,255,0.1);font-size:14px;letter-spacing:12px;text-transform:uppercase;z-index:10;pointer-events:none;user-select:none;font-family:'Courier New',monospace}

        /* ── Toggle ── */
        #ui-toggle{position:fixed;top:20px;right:20px;z-index:1000;width:46px;height:46px;border-radius:50%;border:1px solid rgba(255,255,255,0.12);background:rgba(10,14,23,0.65);backdrop-filter:blur(12px);color:rgba(255,255,255,0.55);font-size:20px;cursor:pointer;display:grid;place-items:center;transition:all 0.3s;user-select:none}
        #ui-toggle:hover{border-color:rgba(0,188,212,0.5);color:#00bcd4;box-shadow:0 0 24px rgba(0,188,212,0.15);transform:scale(1.07)}
        #ui-toggle svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}

        /* ── HUD ── */
        #hud{position:fixed;left:20px;top:50%;transform:translateY(-50%);z-index:998;display:flex;flex-direction:column;gap:10px}
        .hud-btn{width:50px;height:50px;border-radius:14px;border:1px solid rgba(255,255,255,0.06);background:rgba(8,12,24,0.6);backdrop-filter:blur(14px);cursor:pointer;display:grid;place-items:center;transition:all 0.25s;position:relative;user-select:none}
        .hud-btn:hover{border-color:rgba(0,188,212,0.25);background:rgba(8,12,24,0.75);transform:scale(1.1)}
        .hud-btn .ico{font-size:17px;line-height:1;color:rgba(255,255,255,0.5);transition:color 0.3s}
        .hud-btn.on .ico{color:#00bcd4}
        .hud-btn .dot{position:absolute;top:7px;right:7px;width:7px;height:7px;border-radius:50%;transition:all 0.3s}
        .hud-btn .dot.on{background:#00bcd4;box-shadow:0 0 8px rgba(0,188,212,0.5)}
        .hud-btn .dot.off{background:rgba(255,255,255,0.1)}
        .hud-btn .tip{position:absolute;left:62px;top:50%;transform:translateY(-50%);font-size:9px;letter-spacing:.8px;text-transform:uppercase;color:rgba(255,255,255,0.25);white-space:nowrap;opacity:0;transition:opacity 0.25s;pointer-events:none;font-family:'Courier New',monospace}
        .hud-btn:hover .tip{opacity:1}

        /* ── Panel ── */
        #ui-panel{position:fixed;top:0;right:0;bottom:0;width:340px;z-index:999;background:rgba(8,12,24,0.88);backdrop-filter:blur(24px);border-left:1px solid rgba(255,255,255,0.06);display:flex;flex-direction:column;transform:translateX(0);transition:transform 0.4s cubic-bezier(0.4,0,0.2,1);will-change:transform;overflow:hidden}
        #ui-panel.hidden{transform:translateX(calc(100% + 8px))}

        .panel-hd{flex-shrink:0;padding:22px 24px 16px;border-bottom:1px solid rgba(255,255,255,0.06);display:flex;align-items:baseline;gap:10px}
        .panel-hd .title{font-size:15px;font-weight:600;letter-spacing:3px;text-transform:uppercase;color:#fff;background:linear-gradient(135deg,#fff 40%,#00bcd4);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
        .panel-hd .sub{font-size:10px;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,0.3)}

        .panel-bd{flex:1;overflow-y:auto;overflow-x:hidden;padding:8px 0 16px;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.08) transparent}
        .panel-bd::-webkit-scrollbar{width:4px}
        .panel-bd::-webkit-scrollbar-track{background:transparent}
        .panel-bd::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px}

        /* ── Section ── */
        .sec{border-bottom:1px solid rgba(255,255,255,0.04)}
        .sec-hd{display:flex;align-items:center;justify-content:space-between;padding:12px 20px;cursor:pointer;user-select:none;transition:background 0.2s}
        .sec-hd:hover{background:rgba(255,255,255,0.02)}
        .sec-hd .lbl{font-size:11px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,0.5)}
        .sec-hd .arr{font-size:8px;color:rgba(255,255,255,0.25);transition:transform 0.25s}
        .sec.open .sec-hd .arr{transform:rotate(90deg)}
        .sec-bd{padding:2px 20px 14px;display:none}
        .sec.open .sec-bd{display:block}

        .subsec{margin:10px 0 0 12px;border-left:1px solid rgba(255,255,255,0.06);padding-left:14px}
        .subsec .subsec-hd{font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:rgba(0,188,212,0.45);padding:6px 0 8px;cursor:pointer;user-select:none;display:flex;align-items:center;gap:6px;transition:color 0.2s}
        .subsec .subsec-hd:hover{color:rgba(0,188,212,0.7)}
        .subsec .subsec-hd .arr{font-size:7px;transition:transform 0.25s}
        .subsec.open .subsec-hd .arr{transform:rotate(90deg)}
        .subsec-bd{display:none}
        .subsec.open .subsec-bd{display:block}

        /* ── Slider ── */
        .ctrl{margin-bottom:10px}
        .ctrl:last-child{margin-bottom:0}
        .ctrl>label{display:block;font-size:10px;letter-spacing:0.8px;text-transform:uppercase;color:rgba(255,255,255,0.35);margin-bottom:5px}
        .ctrl-row{display:flex;align-items:center;gap:10px}
        .ctrl-row input[type=range]{-webkit-appearance:none;appearance:none;flex:1;height:3px;border-radius:2px;background:rgba(255,255,255,0.1);outline:none;cursor:pointer;transition:background 0.2s}
        .ctrl-row input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:#00bcd4;border:2px solid rgba(10,14,23,0.9);cursor:pointer;transition:all 0.15s;box-shadow:0 0 12px rgba(0,188,212,0.25)}
        .ctrl-row input[type=range]::-webkit-slider-thumb:hover{transform:scale(1.15);box-shadow:0 0 20px rgba(0,188,212,0.4)}
        .ctrl-row input[type=range]::-moz-range-thumb{width:14px;height:14px;border-radius:50%;background:#00bcd4;border:2px solid rgba(10,14,23,0.9);cursor:pointer}
        .ctrl-val{min-width:36px;text-align:right;font-size:11px;font-weight:500;color:rgba(255,255,255,0.5);font-variant-numeric:tabular-nums}
        .ctrl-row input[type=range]:hover{background:rgba(255,255,255,0.16)}

        /* ── Toggle ── */
        .tgl{display:inline-flex;align-items:center;gap:8px;cursor:pointer;user-select:none}
        .tgl input{display:none}
        .tgl .track{position:relative;width:34px;height:20px;background:rgba(255,255,255,0.1);border-radius:10px;transition:background 0.25s}
        .tgl .track::after{content:'';position:absolute;top:3px;left:3px;width:14px;height:14px;border-radius:50%;background:rgba(255,255,255,0.4);transition:all 0.25s}
        .tgl input:checked+.track{background:#00bcd4}
        .tgl input:checked+.track::after{left:17px;background:#fff}
        .tgl .tgl-lbl{font-size:10px;letter-spacing:0.8px;text-transform:uppercase;color:rgba(255,255,255,0.35)}

        /* ── Color ── */
        .clr{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
        .clr label{font-size:10px;letter-spacing:0.8px;text-transform:uppercase;color:rgba(255,255,255,0.35)}
        .clr input[type=color]{-webkit-appearance:none;appearance:none;width:32px;height:32px;border-radius:50%;border:2px solid rgba(255,255,255,0.1);cursor:pointer;padding:0;background:none;transition:border-color 0.2s}
        .clr input[type=color]::-webkit-color-swatch-wrapper{padding:0}
        .clr input[type=color]::-webkit-color-swatch{border:none;border-radius:50%}
        .clr input[type=color]::-moz-color-swatch{border:none;border-radius:50%}
        .clr input[type=color]:hover{border-color:#00bcd4}

        /* ── Buttons ── */
        .ui-btn{display:block;width:100%;padding:10px 16px;margin-bottom:6px;font-size:10px;letter-spacing:1.2px;text-transform:uppercase;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:6px;color:rgba(255,255,255,0.45);cursor:pointer;transition:all 0.2s;font-family:inherit}
        .ui-btn:hover{background:rgba(0,188,212,0.08);border-color:rgba(0,188,212,0.25);color:#00bcd4}
        .ui-btn.primary{background:rgba(0,188,212,0.1);border-color:rgba(0,188,212,0.2);color:#00bcd4}
        .ui-btn.primary:hover{background:rgba(0,188,212,0.18);border-color:rgba(0,188,212,0.35)}
        .ui-btn-row{display:flex;gap:6px;padding:12px 20px 6px;flex-wrap:wrap}
        .ui-btn-row .ui-btn{flex:1;min-width:0}
      `}</style>
    </div>
  );
}
